/**
 * @voltxampere/ui — EraseTool (Sprint 13)
 *
 * Hover to preview, click to delete. The hover target is exposed
 * as a `erase-preview` SceneNode so the renderer can paint it red
 * (Sprint 13 simplification — Wave C wires the actual draw helper).
 *
 *   pointermove → hover-target update (red preview)
 *   pointerdown → DeleteCommand, hover stays for the next target
 *   Esc         → exit, swap to SelectTool
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { DeleteCommand } from "../commands/index.js";
import { componentBounds, wireBounds } from "../scene.js";
export class EraseTool {
    id = "erase";
    cursor = "crosshair";
    hoverTarget = null;
    previewNodeId = "vxa-erase-preview";
    onExit;
    constructor(opts = {}) {
        if (opts.onExit !== undefined)
            this.onExit = opts.onExit;
    }
    onPointerMove(e, ctx) {
        const circuit = ctx.circuit.value;
        if (!circuit)
            return;
        const world = ctx.worldFromEvent(e);
        const hit = ctx.hitTester.hit(world);
        if (!hit) {
            if (this.hoverTarget !== null) {
                this.hoverTarget = null;
                ctx.invalidate();
            }
            return;
        }
        if (hit.kind === "component") {
            const next = {
                kind: "component",
                id: hit.id,
                bounds: componentBounds(hit.instance),
            };
            if (this.hoverTarget?.id !== next.id) {
                this.hoverTarget = next;
                ctx.invalidate();
            }
        }
        else if (hit.kind === "wire") {
            const next = { kind: "wire", id: hit.id, bounds: wireBounds(hit.wire) };
            if (this.hoverTarget?.id !== next.id) {
                this.hoverTarget = next;
                ctx.invalidate();
            }
        }
    }
    onPointerDown(e, ctx) {
        if (e.button !== 0)
            return;
        const circuit = ctx.circuit.value;
        if (!circuit || !this.hoverTarget)
            return;
        const target = { kind: this.hoverTarget.kind, id: this.hoverTarget.id };
        ctx.circuit.value = ctx.undoStack.execute(new DeleteCommand([target], circuit), circuit);
        this.hoverTarget = null;
        ctx.invalidate();
    }
    onKeyDown(e, ctx) {
        if (e.key === "Escape") {
            this.hoverTarget = null;
            ctx.invalidate();
            if (this.onExit)
                this.onExit();
        }
    }
    onLeave(ctx) {
        this.hoverTarget = null;
        ctx.invalidate();
    }
    /** Renderer surface — emit the red preview SceneNode. */
    previewNodes() {
        if (!this.hoverTarget)
            return [];
        return [
            {
                kind: "erase-preview",
                id: this.previewNodeId,
                targetKind: this.hoverTarget.kind,
                targetId: this.hoverTarget.id,
                bounds: this.hoverTarget.bounds,
            },
        ];
    }
    /** Test surface. */
    get hoveredId() {
        return this.hoverTarget?.id ?? null;
    }
}
//# sourceMappingURL=erase.js.map